import { useState, useMemo, useEffect } from "react";

import { Column, Row, Slider, TagTextAlert, Text } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";
import { BalanceDisplay } from "app/components/common";
import debounce from "lodash.debounce";

import { useUpdateWithdrawAmount, useWithdrawAmounts, useWithdrawRaydiumBalances } from "./util";
import { TokenInputs } from "../common";
import { useRaydiumContext } from "../RaydiumContext";
import { useOpenPositionDepositRatio } from "../open/util";
import { getActionTypeInitalized } from "../../clmm/util";

export default function WithdrawAmounts() {
    const { A, B } = useWithdrawRaydiumBalances();
    const { withdrawForm, isActionTypeInitialized } = useRaydiumContext();
    const { tokenAAmount, tokenBAmount } = useWithdrawAmounts();

    const handleChange = useUpdateWithdrawAmount();
    const depositRatio = useOpenPositionDepositRatio();

    return (
        <Column spacing={2}>
            <WithdrawAlert />
            <WithdrawSlider />

            <TokenInputs
                handleChange={handleChange}
                form={{
                    status: withdrawForm.status,
                    tokenAAmount,
                    tokenBAmount
                }}
                disabled={!getActionTypeInitalized("withdraw", isActionTypeInitialized)}
                ratio={depositRatio}
                isFetching={withdrawForm.status === "refetching"}
                getBelowInput={(side) =>
                    side === "A" ? (
                        <BalanceDisplay
                            balance={A.balance}
                            insufficientBalance={A.insufficientBalance}
                            isLoading={A.isLoading}
                            metadata={A.tokenBalance?.metadata}
                            hideInsufficientBalance={true}
                            setMax={(max) => handleChange(max, "A")}
                            disabled={!getActionTypeInitalized("withdraw", isActionTypeInitialized)}
                        />
                    ) : (
                        <BalanceDisplay
                            balance={B.balance}
                            insufficientBalance={B.insufficientBalance}
                            isLoading={B.isLoading}
                            metadata={B.tokenBalance?.metadata}
                            hideInsufficientBalance={true}
                            setMax={(max) => handleChange(max, "B")}
                            disabled={!getActionTypeInitalized("withdraw", isActionTypeInitialized)}
                        />
                    )
                }
            />
        </Column>
    );
}

function WithdrawSlider() {
    const [focused, setFocused] = useState(false);
    const [localValue, setLocalValue] = useState<number | undefined>(undefined);
    const {
        withdrawForm: { percentToWithdraw },
        isActionTypeInitialized,
        setWithdrawForm
    } = useRaydiumContext();
    const { maxWithdrawPercent } = useWithdrawAmounts();

    const setValueDebounced = useMemo(
        () =>
            debounce((value: number) => {
                setWithdrawForm((prev) => ({
                    ...prev,
                    status: "refetch-needed",
                    percentToWithdraw: value
                }));
            }, 200),
        [setWithdrawForm]
    );

    useEffect(() => {
        return () => {
            setValueDebounced.cancel();
        };
    }, [setValueDebounced]);

    return (
        <Column>
            <Row spaceBetween>
                <Text variant="body2" color="caption">
                    Percent to withdraw
                </Text>
                <Text variant="body2">{formatPercent(percentToWithdraw)}</Text>
            </Row>
            <Slider
                onFocus={() => {
                    setFocused(true);
                    setLocalValue(percentToWithdraw);
                }}
                onBlur={() => setFocused(false)}
                step={0.001}
                min={0}
                disabled={maxWithdrawPercent === 0 || !getActionTypeInitalized("withdraw", isActionTypeInitialized)}
                max={maxWithdrawPercent}
                value={focused ? localValue : percentToWithdraw}
                setValue={(multiplier) => {
                    setLocalValue(multiplier);
                    setValueDebounced(multiplier);
                }}
            />
        </Column>
    );
}

function WithdrawAlert() {
    const { maxWithdrawPercent } = useWithdrawAmounts();
    if (maxWithdrawPercent) return null;
    return (
        <TagTextAlert icon="warning" color="warning">
            Your loan-to-value is too high to withdraw collateral
        </TagTextAlert>
    );
}
